/**
 * syncFields - Limpieza de campos locales antes de enviar a Supabase
 *
 * Módulo PURO compartido entre la app (SyncManager) y el Service Worker (sw.ts).
 * Quita campos que existen solo localmente y NO deben viajar a Supabase:
 *  - `sync_status`: tracking local de sync (todas las tablas).
 *  - `is_deleted`: solo se quita para las tablas que NO tienen esa columna en
 *    Supabase (las que sí la tienen la conservan, porque es un soft-delete real
 *    en el backend).
 *
 * No depende de `window` ni del cliente Supabase, así que el SW puede importarlo
 * en su bundle standalone (InjectManifest) sin arrastrar lógica de UI.
 */

/**
 * Campos que SIEMPRE se quitan, en todas las tablas, antes de enviar a Supabase.
 */
export const ALWAYS_STRIP_FIELDS: string[] = ['sync_status'];

/**
 * Tablas de Supabase que tienen la columna `is_deleted` (soft-delete real).
 * Para estas tablas el campo `is_deleted` SÍ viaja a Supabase. Para el resto se
 * elimina antes del envío.
 *
 * NOTA: `animales` también tiene `is_deleted` en Supabase, pero NO figura acá
 * a propósito: el cliente nunca setea su `is_deleted` (lo hace el trigger
 * `sync_animales()` desde animales_historico), así que el campo se strippea
 * antes de pushear y el backend es el único que lo mutatea. `animales_historico`
 * SÍ figura: la corrección bulk de eventos históricos (`AnimalRepository.bulkUpdate`)
 * soft-deletea los eventos viejos emitiendo un BULK_UPDATE `{id, is_deleted:true}`
 * (merge upsert por id) — sin listarla, `stripLocalFields` quitaría `is_deleted`
 * y el upsert llegaría con solo la PK (no-op). El trigger AFTER UPDATE
 * `on_animal_historico_soft_delete_stmt` recomputa `animales` en el backend.
 */
export const TABLES_WITH_IS_DELETED_IN_SUPABASE: string[] = [
  'notas',
  'nota_anclas',
  'personas_campo',
  'gastos',
  'ingresos',
  'stocks_historico',
  'eventos',
  'recordatorios',
  'lluvias',
  'elementos_mapa',
  'puntos_control',
  'puntos_control_eventos',
  'siembras_lote',
  'sigsa_declaraciones',
  'operacion_compra_venta_animales',
  'ubicaciones',
  'unidades',
  'animales_historico'
];

/**
 * Devuelve una copia del registro sin los campos locales.
 * No muta el registro original.
 */
export function stripLocalFields(
  record: Record<string, any>,
  table?: string
): Record<string, any> {
  const cleaned = { ...record };

  for (const field of ALWAYS_STRIP_FIELDS) {
    delete cleaned[field];
  }

  // Quitar is_deleted solo para las tablas que NO lo tienen en Supabase.
  if (table && !TABLES_WITH_IS_DELETED_IN_SUPABASE.includes(table)) {
    delete cleaned.is_deleted;
  }

  return cleaned;
}
